import { Button, Flex, spaces } from '@electricjs/arc';
import styled from 'styled-components';

import type { QuickPrompt } from '@/constants/quickPrompts';

// Arc's ButtonLabel (2 levels deep inside Button) sets white-space: nowrap,
// overflow: hidden, and text-overflow: ellipsis. Override all three so long
// prompt labels wrap to multiple lines instead of overflowing the panel.
const WrappableButton = styled(Button)`
  height: auto;

  span {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    text-align: left;
  }
`;

const MAX_DISPLAY = 4;

type QuickPromptsProps = {
  prompts: QuickPrompt[];
  onSelect: (message: string) => void;
  disabled?: boolean;
};

/**
 * Renders up to 4 quick prompt buttons vertically.
 * Used in Gigawatt chat when there is no conversation yet (empty state).
 */
export function QuickPrompts({
  prompts,
  onSelect,
  disabled = false,
}: QuickPromptsProps) {
  const toShow = prompts
    .slice(0, MAX_DISPLAY)
    .filter(
      (p): p is QuickPrompt & { id: string } =>
        p.id !== undefined && p.id !== null
    );
  if (toShow.length === 0) return null;

  return (
    <Flex
      vertical
      gap={spaces.space_s_rem}
      width="100%"
      role="list"
      aria-label="Quick prompts">
      {toShow.map(prompt => (
        <Flex key={prompt.id} role="listitem">
          <WrappableButton
            id={`quick-prompt-${prompt.id}`}
            data-testid={`quick-prompt-${prompt.id}`}
            variant="outline"
            onClick={() => onSelect(prompt.message)}
            disabled={disabled}
            style={{
              justifyContent: 'flex-start',
              textAlign: 'left',
              width: '100%',
            }}>
            {prompt.label}
          </WrappableButton>
        </Flex>
      ))}
    </Flex>
  );
}
